// Number formatting (docs/07 §5): full with commas up to 999,999, then suffixes with 3 significant
// digits (M, B, T, Qa, Qi, Sx, Sp, Oc, No, Dc, then aa, ab, …), or scientific / engineering.
import { Decimal } from '../sim/decimal';
import type { NumberNotation } from '../sim/types';

const SUFFIXES = ['', 'K', 'M', 'B', 'T', 'Qa', 'Qi', 'Sx', 'Sp', 'Oc', 'No', 'Dc'];

function letterSuffix(i: number): string {
  // i = 0 → 'aa' (1e36), 1 → 'ab', …
  const a = 'a'.charCodeAt(0);
  const first = Math.floor(i / 26);
  const second = i % 26;
  if (first >= 26) return '';
  return String.fromCharCode(a + first) + String.fromCharCode(a + second);
}

function sig3(mantissa: number): string {
  // mantissa in [1, 1000): 3 significant digits, trimmed
  const digits = mantissa >= 100 ? 0 : mantissa >= 10 ? 1 : 2;
  const s = mantissa.toFixed(digits);
  return s.includes('.') ? s.replace(/0+$/, '').replace(/\.$/, '') : s;
}

function withCommas(n: number): string {
  const neg = n < 0;
  const s = Math.floor(Math.abs(n)).toString();
  let out = '';
  for (let i = 0; i < s.length; i++) {
    if (i > 0 && (s.length - i) % 3 === 0) out += ',';
    out += s[i];
  }
  return (neg ? '-' : '') + out;
}

export function formatNumber(value: Decimal | number | string, notation: NumberNotation = 'suffix'): string {
  const d = value instanceof Decimal ? value : new Decimal(value);
  if (d.lt(0)) return '-' + formatNumber(d.neg(), notation);
  if (d.lt(1e6)) {
    const n = d.toNumber();
    if (n > 0 && n < 10 && !Number.isInteger(n)) return sig3(n);
    return withCommas(n);
  }
  const exp = d.log10().floor().toNumber();
  if (!Number.isFinite(exp) || exp > 1e15) return d.toString();

  if (notation === 'suffix') {
    const group = Math.floor(exp / 3);
    let suffix = group < SUFFIXES.length ? SUFFIXES[group] : letterSuffix(group - SUFFIXES.length);
    if (suffix === '' && group >= SUFFIXES.length) return formatNumber(d, 'scientific');
    suffix = suffix ?? '';
    const mant = d.div(Decimal.pow(10, group * 3)).toNumber();
    // rounding can push 999.5 → 1000; roll over to the next group
    if (Number(sig3(mant)) >= 1000) return formatNumber(Decimal.pow(10, (group + 1) * 3), notation);
    return sig3(mant) + suffix;
  }
  if (notation === 'engineering') {
    const e3 = Math.floor(exp / 3) * 3;
    const mant = d.div(Decimal.pow(10, e3)).toNumber();
    return `${sig3(mant)}e${e3}`;
  }
  const mant = d.div(Decimal.pow(10, exp)).toNumber();
  return `${mant.toFixed(2)}e${exp}`;
}

export function formatMult(x: number): string {
  if (x >= 1000) return formatNumber(x);
  return Number.isInteger(x) ? String(x) : x.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

export function formatPercent(p: number): string {
  const v = p * 100;
  return `${v >= 10 || Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1)}%`;
}
